import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, List, Select } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import type {
  MarketFilterOptions,
  MarketProject,
  MarketProjectStatistics,
  ProjectsDataFilter,
} from '../../types/market';
import {
  projectCategoryLabel,
  projectListSubtitle,
  sanitizeFilterForCategory,
  sortMarketProjects,
} from '../../utils/marketHelpers';

export interface ProjectSidebarProps {
  projects: MarketProject[];
  statistics: MarketProjectStatistics;
  filter: ProjectsDataFilter;
  setFilter: Dispatch<SetStateAction<ProjectsDataFilter>>;
  filterOptions: MarketFilterOptions;
  selectedProjectId: string | null | undefined;
  onSelectProject: (projectId: string) => void;
  /** 侧栏重复点击当前已选中项时调用（用于地图再次「跟随」到该项目） */
  onReselectSameProject?: (projectId: string) => void;
  onProjectCategoryChange?: () => void;
}

function MultiSelect(props: {
  placeholder: string;
  value: string[] | undefined;
  options: string[];
  onChange: (values: string[]) => void;
}): React.ReactElement {
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      placeholder={props.placeholder}
      className="dash-select"
      dropdownClassName="dash-select-dropdown"
      style={{ width: '100%' }}
      value={props.value}
      onChange={props.onChange}
      options={props.options.map((t) => ({ label: t, value: t }))}
    />
  );
}

export function ProjectSidebar(props: ProjectSidebarProps): React.ReactElement {
  const {
    projects,
    statistics,
    filter,
    setFilter,
    filterOptions,
    selectedProjectId,
    onSelectProject,
    onReselectSameProject,
    onProjectCategoryChange,
  } = props;

  const sortedProjects = useMemo(
    () => sortMarketProjects(projects),
    [projects],
  );
  const [keywordInput, setKeywordInput] = useState(filter.keyword ?? '');

  useEffect(() => {
    const kw = keywordInput.trim();
    setFilter((prev) => ({
      ...prev,
      keyword: kw || undefined,
    }));
  }, [keywordInput, setFilter]);

  const listProjects = sortedProjects;

  return (
    <aside className="dash-panel dash-panel--left">
      <div className="dash-section">
        <StatGrid statistics={statistics} />
      </div>

      <div className="dash-section">
        <div className="dash-field-label">项目搜索</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={keywordInput}
            placeholder="请输入项目关键字"
            onChange={(e) => setKeywordInput(e.target.value)}
          />
          <Button
            type="default"
            disabled={!keywordInput.trim()}
            onClick={() => setKeywordInput('')}
          >
            清空
          </Button>
        </div>

        <div className="dash-field-label">项目类别</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.category}
          onChange={(v) => {
            setFilter((prev) => sanitizeFilterForCategory(prev, v));
            onProjectCategoryChange?.();
          }}
          options={[
            { label: '全部', value: 'all' },
            { label: '前期项目', value: 'preliminary' },
            { label: '履约项目', value: 'performance' },
          ]}
        />

        {filter.category === 'preliminary' && (
          <>
            <div className="dash-field-label dash-field-label--spaced">
              行业类别
            </div>
            <MultiSelect
              placeholder="可多选行业"
              value={filter.industries}
              options={filterOptions.industries}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  industries: v.length ? v : undefined,
                }))
              }
            />
            <div className="dash-field-label dash-field-label--spaced">
              所属区域
            </div>
            <MultiSelect
              placeholder="可多选区域"
              value={filter.regions}
              options={filterOptions.regions}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  regions: v.length ? v : undefined,
                }))
              }
            />
          </>
        )}

        {filter.category === 'performance' && (
          <>
            <div className="dash-field-label dash-field-label--spaced">
              业务类型
            </div>
            <MultiSelect
              placeholder="可多选业务类型"
              value={filter.businessTypes}
              options={filterOptions.businessTypes}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  businessTypes: v.length ? v : undefined,
                }))
              }
            />
            <div className="dash-field-label dash-field-label--spaced">
              责任科室
            </div>
            <MultiSelect
              placeholder="可多选科室"
              value={filter.departments}
              options={filterOptions.departments}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  departments: v.length ? v : undefined,
                }))
              }
            />
          </>
        )}
      </div>

      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">项目列表</div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={listProjects}
            locale={{ emptyText: '暂无项目数据' }}
            renderItem={(project) => {
              const isSelected = selectedProjectId === project.id;
              const categoryClass =
                project.category === 'preliminary'
                  ? 'dash-list-item--preliminary'
                  : 'dash-list-item--performance';
              return (
                <List.Item
                  className={
                    isSelected
                      ? `dash-list-item dash-list-item--active ${categoryClass}`
                      : `dash-list-item ${categoryClass}`
                  }
                  onClick={() => {
                    if (selectedProjectId === project.id) {
                      onReselectSameProject?.(project.id);
                    }
                    onSelectProject(project.id);
                  }}
                >
                  <div className="dash-list-item__stack">
                    <div className="dash-list-item__title-row">
                      <span className="dash-list-item__name">{project.name}</span>
                      {project.isNonEnergyPower && (
                        <span className="dash-list-item__badge dash-list-item__badge--overseas">
                          非能源电力
                        </span>
                      )}
                    </div>
                    <span className="dash-list-item__meta">
                      {projectCategoryLabel(project.category)} ·{' '}
                      {projectListSubtitle(project)}
                    </span>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}

function StatGrid(props: {
  statistics: MarketProjectStatistics;
}): React.ReactElement {
  const { statistics } = props;
  return (
    <div className="dash-stat-grid dash-stat-grid--market">
      <div className="dash-stat-card">
        <div className="dash-stat-card__label">前期项目数量</div>
        <div className="dash-stat-card__value dash-stat-card__value--preliminary">
          {statistics.preliminary}
        </div>
      </div>
      <div className="dash-stat-card">
        <div className="dash-stat-card__label">履约项目数量</div>
        <div className="dash-stat-card__value dash-stat-card__value--performance">
          {statistics.performance}
        </div>
      </div>
    </div>
  );
}
